import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Package, Search, Star, Zap, Video, Palette, Globe, Megaphone,
  CheckCircle2, ArrowRight, Sparkles, Clock, Users, Rocket, Euro
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const SERVICE_CATEGORIES = [
  { id: 'all', label: 'Tous', icon: Package },
  { id: 'video', label: 'Vidéo', icon: Video },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'web', label: 'Web', icon: Globe },
  { id: 'marketing', label: 'Marketing', icon: Megaphone }
];

const OffersPage = () => {
  const { token, user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copilotePackages, setCopilotePackages] = useState([]);
  
  // Default services
  const [services] = useState([
    {
      id: '1',
      name: 'Production Vidéo',
      description: 'Création de contenus vidéo professionnels pour votre marque',
      category: 'video',
      features: ['Spots publicitaires', 'Vidéos corporate', 'Motion design', 'Montage & post-production'],
      starting_price: 1500,
      popular: true,
      delivery: '2-4 semaines'
    },
    {
      id: '2',
      name: 'Identité Visuelle',
      description: 'Création ou refonte complète de votre identité de marque',
      category: 'branding',
      features: ['Logo & charte graphique', 'Supports print', 'Templates réseaux sociaux', 'Guide de marque'],
      starting_price: 2500,
      popular: true,
      delivery: '3-5 semaines'
    },
    {
      id: '3',
      name: 'Site Web',
      description: 'Conception et développement de sites web sur-mesure',
      category: 'web',
      features: ['Design responsive', 'Optimisation SEO', 'CMS intégré', 'Formation incluse'],
      starting_price: 3000,
      popular: false,
      delivery: '4-8 semaines'
    },
    {
      id: '4',
      name: 'Community Management',
      description: 'Gestion complète de vos réseaux sociaux',
      category: 'marketing',
      features: ['Stratégie éditoriale', 'Création de contenus', 'Modération', 'Reporting mensuel'],
      starting_price: 800,
      monthly: true,
      popular: false,
      delivery: 'Mensuel'
    },
    {
      id: '5',
      name: 'Campagne Publicitaire',
      description: 'Création et gestion de vos campagnes Ads',
      category: 'marketing',
      features: ['Audit & stratégie', 'Création visuels', 'Gestion des campagnes', 'Optimisation continue'],
      starting_price: 1200,
      monthly: true,
      popular: false,
      delivery: 'Mensuel'
    },
    {
      id: '6',
      name: 'Photos Produits',
      description: 'Shootings photos professionnels pour vos produits',
      category: 'video',
      features: ['Photos packshot', 'Photos ambiance', 'Retouches incluses', 'Formats multiples'],
      starting_price: 500,
      popular: false,
      delivery: '1-2 semaines'
    }
  ]);

  // Fetch COPILOTE packages
  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/pricing/copilote-packages/');
      setCopilotePackages(response.data || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const filteredServices = services.filter(service => {
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getCategoryIcon = (category) => {
    const cat = SERVICE_CATEGORIES.find(c => c.id === category);
    return cat ? cat.icon : Package;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        <Header setMobileOpen={setMobileOpen} />
        
        <main className="flex-1 p-4 md:p-6 pt-32 overflow-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Offres & Services</h1>
            <p className="text-muted-foreground">Découvrez nos prestations et forfaits</p>
          </div>

          {/* COPILOTE Banner */}
          {!user?.copilote_subscription?.active && (
            <Card className="mb-6 bg-gradient-to-r from-orange-500 to-red-500 text-white border-0">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Rocket className="h-8 w-8" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        COPILOTE
                        <Badge className="bg-white/20 text-white">Premium</Badge>
                      </h2>
                      <p className="text-white/80">Accompagnement personnalisé pour booster votre ROI</p>
                    </div>
                  </div>
                  <Button className="bg-white text-orange-600 hover:bg-white/90">
                    Découvrir <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* COPILOTE Packages */}
          {copilotePackages.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Rocket className="h-5 w-5 text-orange-500" />
                Forfaits COPILOTE
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {copilotePackages.map((pkg, index) => (
                  <Card key={pkg.name} className={cn(
                    "relative overflow-hidden transition-all hover:shadow-lg",
                    index === 2 && "ring-2 ring-orange-500"
                  )}>
                    {index === 2 && (
                      <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs px-2 py-1 rounded-bl-lg">
                        Populaire
                      </div>
                    )}
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">{pkg.name}</h3>
                      <div className="text-2xl font-bold text-orange-600 mb-1">
                        {formatCurrency(pkg.total_price || 0)}
                      </div>
                      {pkg.hours > 0 && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {pkg.hours}h à {formatCurrency(pkg.price_per_hour)}/h
                        </p>
                      )}
                      {pkg.savings > 0 && (
                        <Badge className="bg-green-100 text-green-700">
                          -{formatCurrency(pkg.savings)} économisés
                        </Badge>
                      )}
                      <Button className="w-full mt-4" variant={index === 2 ? 'default' : 'outline'}>
                        Choisir
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            {SERVICE_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                  className="gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {category.label}
                </Button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 max-w-md"
            />
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => {
              const CategoryIcon = getCategoryIcon(service.category);
              return (
                <Card key={service.id} className={cn(
                  "relative overflow-hidden transition-all hover:shadow-lg",
                  service.popular && "ring-2 ring-primary"
                )}>
                  {service.popular && (
                    <div className="absolute top-0 right-0 bg-primary text-white text-xs px-2 py-1 rounded-bl-lg flex items-center gap-1">
                      <Star className="h-3 w-3" /> Populaire
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <CategoryIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{service.name}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">{service.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground">À partir de</p>
                        <p className="text-xl font-bold">
                          {formatCurrency(service.starting_price)}
                          {service.monthly && <span className="text-sm font-normal text-muted-foreground">/mois</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" />
                          {service.delivery}
                        </div>
                      </div>
                    </div>
                    <Button className="w-full mt-4">
                      Demander un devis <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact CTA */}
          <Card className="mt-8">
            <CardContent className="p-6 text-center">
              <Sparkles className="h-12 w-12 mx-auto text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Besoin d'un service sur-mesure ?</h3>
              <p className="text-muted-foreground mb-4">Notre équipe est là pour créer la solution adaptée à vos besoins.</p>
              <Button size="lg">
                Nous contacter
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default OffersPage;
